import { normalizeForSearch, buildSearchHaystack, matchesSearchTokens, type SearchableListing } from "@/lib/listing-search"
import { canonicalCityKey, formatCity, formatState } from "@/lib/format-location"
import { countryCodeForState, countryForState } from "@/lib/country-from-state"

/**
 * Parses a free-text "help me find a barbershop to work at in chicago
 * that's hiring" style query into structured filters, then applies them
 * against the same public /api/shops feed the /listings page already
 * fetches — no separate search backend, no per-query API cost.
 *
 * Deliberately not LLM-backed: every query here is free and instant, and
 * the filters below (city/state/country, price, booth count, style
 * keywords) cover what a barber actually searches by. What it can't do is
 * synonym expansion — "minimal" only matches a listing whose own
 * description/amenities use that word — which is the honest tradeoff for
 * a zero-cost, zero-latency search.
 */

// Words that carry no filtering signal on their own. Every remaining word
// after this strip is AND-matched against the listing (matchesSearchTokens
// requires all tokens to hit), so a filler word like "help" surviving here
// would zero out every result.
const STOPWORDS = new Set([
  "help", "me", "find", "a", "an", "the", "to", "at", "in", "on", "for", "of",
  "is", "are", "im", "i'm", "i", "looking", "look", "want", "wanna", "need",
  "please", "pls", "plz", "thats", "that's", "who's", "whos", "near", "nearby",
  "around", "with", "and", "or", "shop", "shops", "barbershop", "barbershops",
  "barber", "barbers", "work", "working", "hiring", "hire",
  "hires", "open", "openings", "opening", "chair", "chairs", "booth", "booths",
  "rent", "rental", "rentals", "station", "stations", "available",
])

export interface ParsedWorkQuery {
  tokens: string[]
  maxMonthly?: number
  minBooths?: number
  maxBooths?: number
}

const UNDER_PRICE = /\b(?:under|below|less than|max|up to)\s*\$?(\d{2,5})/i
const BARE_PRICE = /\$(\d{2,5})/
// A price alone ("$250") is ambiguous between a monthly, weekly, or daily
// rent — "$250 week", "$250/week" and "$250 a week" all mean the SAME
// $250/week, not $250/month. Whatever unit trails the number gets converted
// to the monthly-equivalent /api/shops itself already computes (weekly*4,
// daily*30 — see app/api/shops/route.ts), so the filter compares apples to
// apples against listing.monthlyPrice. No trailing unit still means monthly,
// same as before.
const UNIT_AFTER = /^[\s/]*(?:a|an|per)?\s*(day|daily|week|weekly|month|monthly)\b/i
const UNIT_TO_MONTHLY: Record<string, number> = {
  day: 30, daily: 30,
  week: 4, weekly: 4,
  month: 1, monthly: 1,
}

function extractPrice(query: string): { maxMonthly?: number; remaining: string } {
  const match = query.match(UNDER_PRICE) ?? query.match(BARE_PRICE)
  if (!match || match.index === undefined) return { remaining: query }

  const matchEnd = match.index + match[0].length
  const unitMatch = query.slice(matchEnd).match(UNIT_AFTER)
  const multiplier = unitMatch ? (UNIT_TO_MONTHLY[unitMatch[1].toLowerCase()] ?? 1) : 1
  const removeLength = unitMatch ? unitMatch[0].length : 0

  const remaining = query.slice(0, match.index) + " " + query.slice(matchEnd + removeLength)
  return { maxMonthly: Number(match[1]) * multiplier, remaining }
}

const BOOTH_COUNT = /\b(\d{1,3})\s*[+]?\s*(?:booths?|chairs?|stations?)\b/i

function extractSize(query: string): { minBooths?: number; maxBooths?: number; remaining: string } {
  const explicit = query.match(BOOTH_COUNT)
  if (explicit) {
    const n = Number(explicit[1])
    const plus = explicit[0].includes("+")
    return plus
      ? { minBooths: n, remaining: query.replace(explicit[0], " ") }
      : { minBooths: n, maxBooths: n, remaining: query.replace(explicit[0], " ") }
  }
  if (/\b(small|solo|one[- ]?chair|tiny)\b/i.test(query)) {
    return { maxBooths: 3, remaining: query.replace(/\b(small|solo|one[- ]?chair|tiny)\b/i, " ") }
  }
  if (/\b(large|big|huge)\b/i.test(query)) {
    return { minBooths: 8, remaining: query.replace(/\b(large|big|huge)\b/i, " ") }
  }
  return { remaining: query }
}

export function parseWorkQuery(raw: string): ParsedWorkQuery {
  const { maxMonthly, remaining: afterPrice } = extractPrice(raw)
  const { minBooths, maxBooths, remaining: afterSize } = extractSize(afterPrice)

  // normalizeForSearch only lowercases/strips accents — a comma or slash
  // stuck to a word ("shop," "chairs,") survives into the token, so it never
  // matches a STOPWORDS entry and lingers as a required (unmatchable)
  // keyword. Trim non-letter/non-number edges per token; an internal
  // hyphen ("walk-in") is untouched since it isn't at either edge.
  const tokens = normalizeForSearch(afterSize)
    .split(/\s+/)
    .map((t) => t.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""))
    .filter(Boolean)
    .filter((t) => !STOPWORDS.has(t))
    // "hiring" stays filler — every result here is hiring by premise, so the
    // word must never exclude a shop that only has open chairs. "job(s)" is
    // different: it asks for a posted position, which jobSearchTerms makes
    // findable, so it's a real token (folded to one spelling).
    .map((t) => (t === "jobs" ? "job" : t))

  return { tokens, maxMonthly, minBooths, maxBooths }
}

/** An open job post on a shop — the richer "hiring" signal beyond a booth count. */
export interface WorkJob {
  id: string
  shop_id: string
  slug?: string | null
  title: string
  position_type: string[]
  compensation: string | null
  specialties: string[]
  experience_required: string | null
}

export interface WorkSearchListing extends SearchableListing {
  id: string
  slug: string
  name: string
  city: string
  state: string
  cover_image?: string | null
  images?: string[]
  monthlyPrice?: number | null
  weeklyPrice?: number | null
  availableBooths?: number
  totalBooths?: number
  shopType?: string | null
  sort_boost?: number | null
  created_at?: string
  hide_rates?: boolean
  /** Enabled rental plans as /api/shops returns them (billing_period or plan_type + price). */
  boothPlans?: { billing_period?: string | null; plan_type?: string | null; price?: number | string | null }[]
  /** Open job posts, attached client-side from job_listings. */
  jobs?: WorkJob[]
}

// ── Hiring vocabulary ──────────────────────────────────────────────────
// Mirrors components/hiring/hiring-form.tsx so a job's stored values read
// the same words on the search card as on /hiring. Kept here (not imported)
// so this module stays free of React.
export const POSITION_LABEL: Record<string, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  weekends: "Weekends",
  flexible: "Flexible",
  contract: "Contract",
}
export const COMPENSATION_LABEL: Record<string, string> = {
  booth_rental: "Booth rental",
  commission: "Commission",
  both: "Booth or commission",
  hourly: "Hourly",
  salary: "Salary",
}
export const SPECIALTY_LABEL: Record<string, string> = {
  fades: "Fades",
  beards: "Beards",
  color: "Color",
  curly_textured: "Curly / textured",
  kids: "Kids",
  long_hair: "Long hair",
  skin_fades: "Skin fades",
  razor_work: "Razor work",
}

export type PlanType = "monthly" | "weekly" | "daily" | "commission"
export const PLAN_LABEL: Record<PlanType, string> = {
  monthly: "Monthly",
  weekly: "Weekly",
  daily: "Daily",
  commission: "Commission",
}

/** The rental cadences a shop offers, deduped, in a stable order. Commission
 *  counts when a job post pays that way — it's how a shop "rents" a chair
 *  without a booth plan. */
export function listingPlanTypes(listing: WorkSearchListing): PlanType[] {
  const found = new Set<PlanType>()
  for (const p of listing.boothPlans ?? []) {
    const period = (p.billing_period || p.plan_type || "").toLowerCase()
    if (period === "monthly" || period === "weekly" || period === "daily") found.add(period)
    if (period === "commission") found.add("commission")
  }
  for (const j of listing.jobs ?? []) {
    if (j.compensation === "commission" || j.compensation === "both") found.add("commission")
  }
  return (["monthly", "weekly", "daily", "commission"] as PlanType[]).filter((t) => found.has(t))
}

/** Monthly-equivalent rent for bucketing; null when hidden or unset. */
export function listingMonthly(listing: WorkSearchListing): number | null {
  if (listing.hide_rates) return null
  if (typeof listing.monthlyPrice === "number" && listing.monthlyPrice > 0) return listing.monthlyPrice
  if (typeof listing.weeklyPrice === "number" && listing.weeklyPrice > 0) return listing.weeklyPrice * 4
  return null
}

// Countries whose region reads naturally after a city ("Chicago, IL",
// "Toronto, ON"). Everywhere else the region is noise to a barber
// ("London, Greater London", "Osaka, Osaka") — the country says more.
const REGION_COUNTRIES = new Set(["US", "CA", "MX"])
const COUNTRY_SHORT: Record<string, string> = { GB: "UK", AE: "UAE" }

/** "Chicago, IL", "London, UK", "CDMX" — one clean display string per real
 *  city. Cased and abbreviated here so "chicago, Illinois" and "Chicago, IL"
 *  read (and group) as one. */
export function listingCityLabel(listing: WorkSearchListing): string {
  const city = formatCity(listing.city || "")
  const state = formatState(listing.state || "")
  // The capital's own name is the region — "Ciudad de México, CDMX" says it twice.
  if (state === "CDMX" && canonicalCityKey(listing.city || "") === "ciudad de méxico") return "CDMX"
  const code = countryCodeForState(listing.state || "")
  if (code && !REGION_COUNTRIES.has(code)) {
    const country = COUNTRY_SHORT[code] ?? countryForState(listing.state || "")?.name ?? state
    return [city, country].filter(Boolean).join(", ")
  }
  return [city, state].filter(Boolean).join(", ")
}
export function listingCityKey(listing: WorkSearchListing): string {
  return `${canonicalCityKey(listing.city || "")}|${formatState(listing.state || "").toLowerCase()}`
}

// ── Facets ─────────────────────────────────────────────────────────────
export type PriceBucket = "under500" | "500to1000" | "over1000"
export const PRICE_BUCKET_LABEL: Record<PriceBucket, string> = {
  under500: "Under $500 / mo",
  "500to1000": "$500 – $1,000 / mo",
  over1000: "Over $1,000 / mo",
}

export interface WorkFacets {
  /** listingCityKey() value, or null for all. */
  cityKey: string | null
  /** Minimum open chairs (1 = any shop with a chair). */
  minOpen: number | null
  planType: PlanType | null
  price: PriceBucket | null
  /** Only shops with an open job post. */
  hiringOnly: boolean
}

export const EMPTY_FACETS: WorkFacets = { cityKey: null, minOpen: null, planType: null, price: null, hiringOnly: false }

export function facetsActive(f: WorkFacets): boolean {
  return f.cityKey !== null || f.minOpen !== null || f.planType !== null || f.price !== null || f.hiringOnly
}

export function matchesFacets(listing: WorkSearchListing, f: WorkFacets): boolean {
  if (f.cityKey && listingCityKey(listing) !== f.cityKey) return false
  if (f.minOpen !== null && (listing.availableBooths ?? 0) < f.minOpen) return false
  if (f.planType && !listingPlanTypes(listing).includes(f.planType)) return false
  if (f.price) {
    const m = listingMonthly(listing)
    // Hidden/unset rent can't be bucketed; a price facet is an explicit ask,
    // so those drop out (unlike the free-text "under $X", which lets them by).
    if (m === null) return false
    if (f.price === "under500" && m >= 500) return false
    if (f.price === "500to1000" && (m < 500 || m > 1000)) return false
    if (f.price === "over1000" && m <= 1000) return false
  }
  if (f.hiringOnly && !(listing.jobs && listing.jobs.length > 0)) return false
  return true
}

/** Every word a job post can be found by — title, role words, pay words,
 *  specialties — so "commission fades" finds the shop that posted for it. */
function jobSearchTerms(jobs: WorkJob[] | undefined): string[] {
  if (!jobs?.length) return []
  const out: string[] = ["job", "position"]
  for (const j of jobs) {
    out.push(j.title)
    for (const p of j.position_type ?? []) out.push(p.replace(/_/g, " "), POSITION_LABEL[p] ?? "")
    if (j.compensation) out.push(j.compensation.replace(/_/g, " "), COMPENSATION_LABEL[j.compensation] ?? "")
    for (const sp of j.specialties ?? []) out.push(sp.replace(/_/g, " "), SPECIALTY_LABEL[sp] ?? "")
    if (j.experience_required) out.push(j.experience_required.replace(/_/g, " "))
  }
  return out.filter(Boolean)
}

/**
 * Every published shop is a candidate, open chair or not. A barber who likes
 * a shop can still reach the owner and get in line for the next chair — so
 * full shops stay in the results, ranked after the ones with a chair open
 * (and after anyone hiring). The MCP search_booths tool makes the same call.
 */
export function filterShopsForWork(
  listings: WorkSearchListing[],
  parsed: ParsedWorkQuery,
  facets: WorkFacets = EMPTY_FACETS,
): WorkSearchListing[] {
  const results = listings.filter((listing) => {
    if (!matchesFacets(listing, facets)) return false

    if (typeof parsed.maxMonthly === "number") {
      const price = listing.hide_rates ? null : listing.monthlyPrice ?? (listing.weeklyPrice ? listing.weeklyPrice * 4 : null)
      // No price on file (or hidden) can't be evaluated — don't punish the
      // listing for it, same call the MCP tool makes.
      if (typeof price === "number" && price > parsed.maxMonthly) return false
    }

    const total = listing.totalBooths ?? 0
    if (typeof parsed.minBooths === "number" && total > 0 && total < parsed.minBooths) return false
    if (typeof parsed.maxBooths === "number" && total > 0 && total > parsed.maxBooths) return false

    if (parsed.tokens.length > 0) {
      const haystack =
        buildSearchHaystack(listing, listing.shopType || undefined) +
        " " +
        normalizeForSearch([...listingPlanTypes(listing), ...jobSearchTerms(listing.jobs)].join(" "))
      if (!matchesSearchTokens(haystack, parsed.tokens)) return false
    }

    return true
  })

  return results.sort((a, b) => {
    const boost = (b.sort_boost ?? 0) - (a.sort_boost ?? 0)
    if (boost !== 0) return boost
    // A posted job is the strongest "we're hiring" a shop can send.
    const jobs = (b.jobs?.length ?? 0) - (a.jobs?.length ?? 0)
    if (jobs !== 0) return jobs
    // Then anyone with a chair open, before shops that are full for now.
    const open = Number((b.availableBooths ?? 0) > 0) - Number((a.availableBooths ?? 0) > 0)
    if (open !== 0) return open
    return (b.availableBooths ?? 0) - (a.availableBooths ?? 0)
  })
}
